import type { ProductContext, RenderNode } from '@inf-monkeys-tech/monkeys';

import type {
  CompiledDesignCapabilityCatalog,
  CompiledDesignCapabilityRuntime,
  DesignCapabilityProviderRegistry,
  DesignCapabilityRendererRegistry,
} from './contracts';

export interface CompileDesignCapabilityRuntimeInput {
  catalog: CompiledDesignCapabilityCatalog;
  providers: DesignCapabilityProviderRegistry;
  renderers: DesignCapabilityRendererRegistry;
}

export const compileDesignCapabilityRuntime = ({
  catalog,
  providers,
  renderers,
}: CompileDesignCapabilityRuntimeInput): CompiledDesignCapabilityRuntime => {
  const providerSnapshot = Object.freeze(
    Object.fromEntries(
      Object.entries(providers).map(([providerId, provider]) => [
        providerId,
        Object.freeze({ ...provider }),
      ]),
    ),
  ) as DesignCapabilityProviderRegistry;
  const rendererSnapshot = Object.freeze({ ...renderers }) as DesignCapabilityRendererRegistry;

  for (const entry of catalog.entries) {
    const provider = providerSnapshot[entry.provider.providerId];
    if (!provider || provider.providerId !== entry.provider.providerId) {
      throw new TypeError(
        `${entry.manifest.id} has no explicit runtime provider ${entry.provider.providerId}.`,
      );
    }
    const expectedAdapterId = entry.provider.sideEffectAdapterRef?.id;
    if (provider.sideEffectAdapterId !== expectedAdapterId) {
      throw new TypeError(
        `${entry.manifest.id} runtime provider must bind side-effect adapter ${expectedAdapterId ?? 'none'}.`,
      );
    }
    if (typeof rendererSnapshot[entry.provider.rendererKey] !== 'function') {
      throw new TypeError(
        `${entry.manifest.id} has no explicit renderer ${entry.provider.rendererKey}.`,
      );
    }
  }

  const resolve = (node: RenderNode, product: ProductContext) => {
    const entry = catalog.resolve(
      node.capabilityRef.id,
      product,
      node.providerRef?.id,
    );
    if (
      node.capabilityRef.kind !== 'capability'
      || node.capabilityRef.ownerRepo !== entry.manifest.ownerRepo
      || String(node.capabilityRef.version) !== String(entry.manifest.capabilityVersion)
    ) {
      throw new TypeError(
        `Capability reference must pin owner ${entry.manifest.ownerRepo} and version ${entry.manifest.capabilityVersion}.`,
      );
    }
    if (
      !node.providerRef
      || node.providerRef.kind !== 'view-provider'
      || node.providerRef.ownerRepo !== entry.provider.ownerRepo
      || String(node.providerRef.version) !== String(entry.provider.providerVersion)
    ) {
      throw new TypeError(
        `Capability provider reference must pin owner ${entry.provider.ownerRepo} and version ${entry.provider.providerVersion}.`,
      );
    }
    return {
      entry,
      provider: providerSnapshot[entry.provider.providerId]!,
      renderer: rendererSnapshot[entry.provider.rendererKey]!,
    };
  };

  return Object.freeze({
    catalog,
    providers: providerSnapshot,
    renderers: rendererSnapshot,
    resolve,
  });
};
